"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FullPageMessage } from "@/components/layouts/FullPageMessage/FullPageMessage";
import {
  getMessages,
  isLocale,
  DEFAULT_LOCALE,
  type Locale,
} from "@/lib/i18n";

/**
 * Root 404 page — fallback gdy `notFound()` zostanie wywołane poza segmentem
 * `[locale]` lub URL nie pasuje do żadnego route'a.
 *
 * **Client Component** żeby uzyskać dostęp do `usePathname()` i wydobyć locale
 * z URL'a (`/pl/...` -> `pl`). Bez tego renderowałby się PL nawet dla visitor'a
 * na `/en/...`.
 *
 * **Routing hierarchy:**
 *   1. `app/[locale]/[...slug]/not-found.tsx` (nie istnieje) ->
 *   2. `app/[locale]/not-found.tsx` (segment-aware, lokalizowane przez params) ->
 *   3. `app/not-found.tsx` (TUTAJ — fallback ostatniej szansy)
 *
 * Renderuje pełnoekranowy `FullPageMessage` analogicznie do segment-level.
 *
 * @see app/[locale]/not-found.tsx — segment-level (preferowany)
 */
export default function RootNotFound() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);
  const localeCandidate = segments[0];
  const locale: Locale = isLocale(localeCandidate)
    ? localeCandidate
    : DEFAULT_LOCALE;

  const t = getMessages(locale).notFound;

  /* 404 reporter - wysyła URL do backend m_seo > Błędy 404 dla audit przez admina.
     URL-e poza segmentem `[locale]` (np. `/ble%20ble%20test` bez locale prefix)
     trafiają TUTAJ, NIE w `app/[locale]/not-found.tsx`. Reporter musi być w obu
     żeby admin widział wszystkie 404 niezależnie od lokalizacji w routing tree.
     Fail-soft - nigdy nie crash'uje 404 page. */
  useEffect(() => {
    fetch("/api/seo/404", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        url: pathname,
        referer: document.referrer || null,
        user_agent: navigator.userAgent,
      }),
    }).catch(() => {
      /* Silent fail - klient widzi już 404 page, nie ma sensu duplikować błędu. */
    });
  }, [pathname]);

  return (
    <FullPageMessage
      title="404"
      subtitle={t.title}
      body={t.body}
      icon="warning"
      variant="info"
      /* `/` zamiast `/${locale}` - tu trafia też 404 z `app/[locale]/layout.tsx`
         dla języka spoza domeny (np. `/de` na domenie z samym `pl`), a link
         `/${locale}` prowadziłby z powrotem na ten sam 404. */
      action={<Link href="/">{t.homeLink}</Link>}
    />
  );
}
